package br.com.htms.inspeccaocampo.data.nrvisual

import br.com.htms.inspeccaocampo.data.FotoAnexo
import br.com.htms.inspeccaocampo.data.JsonMap
import br.com.htms.inspeccaocampo.platform.currentTimeMillis
import br.com.htms.inspeccaocampo.platform.randomUuid

object NrVisualJson {
    fun toJson(form: FormAnaliseVisualNr): String = buildString {
        append('{')
        field("tstResponsavel", form.tstResponsavel); append(',')
        field("obra", form.obra); append(',')
        field("localTexto", form.localTexto); append(',')
        field("descricaoCena", form.descricaoCena); append(',')
        field("cenaTipo", form.cenaTipo.id); append(',')
        append("\"fotos\":"); append(fotosToJson(form.fotos)); append(',')
        append("\"achados\":[")
        form.achados.forEachIndexed { i, a ->
            if (i > 0) append(',')
            append(achadoToJson(a))
        }
        append(']')
        if (form.analisadoEm != null) {
            append(','); append("\"analisadoEm\":"); append(form.analisadoEm)
        }
        append(','); field("modoAnalise", form.modoAnalise)
        append(','); field("normaVersao", form.normaVersao)
        append(','); field("normaVersaoData", form.normaVersaoData)
        append(','); field("observacoesTst", form.observacoesTst)
        append(','); append("\"objetosDetectados\":[")
        form.objetosDetectados.forEachIndexed { i, o ->
            if (i > 0) append(',')
            append('"'); append(escape(o)); append('"')
        }
        append(']')
        append(','); field("resumoCena", form.resumoCena)
        append(','); append("\"descricoesFotos\":[")
        form.descricoesFotos.forEachIndexed { i, d ->
            if (i > 0) append(',')
            append('{')
            field("fotoId", d.fotoId); append(',')
            append("\"numero\":"); append(d.numero); append(',')
            field("texto", d.texto); append(',')
            append("\"objetos\":[")
            d.objetos.forEachIndexed { j, o ->
                if (j > 0) append(',')
                append('"'); append(escape(o)); append('"')
            }
            append("]}")
        }
        append(']')
        append(','); append("\"perguntas\":[")
        form.perguntas.forEachIndexed { i, p ->
            if (i > 0) append(',')
            append('{')
            field("id", p.id); append(',')
            field("fotoId", p.fotoId); append(',')
            append("\"fotoNumero\":"); append(p.fotoNumero); append(',')
            field("objeto", p.objeto); append(',')
            field("texto", p.texto); append(',')
            field("resposta", p.resposta)
            append('}')
        }
        append(']')
        append(','); append("\"dataHorarioMs\":"); append(form.dataHorarioMs)
        if (form.latitude != null) {
            append(','); append("\"latitude\":"); append(form.latitude)
        }
        if (form.longitude != null) {
            append(','); append("\"longitude\":"); append(form.longitude)
        }
        append('}')
    }

    fun fromJson(json: String): FormAnaliseVisualNr {
        val root = JsonMap.parse(json)
        return FormAnaliseVisualNr(
            tstResponsavel = root.str("tstResponsavel"),
            obra = root.str("obra"),
            localTexto = root.str("localTexto"),
            descricaoCena = root.str("descricaoCena"),
            cenaTipo = NrCenaTipo.fromId(root.str("cenaTipo")),
            fotos = lerFotos(root.array("fotos")),
            achados = lerAchados(root.array("achados")),
            analisadoEm = root.long("analisadoEm"),
            modoAnalise = root.str("modoAnalise"),
            normaVersao = root.str("normaVersao").ifBlank { NrVisualCatalog.NORMA_VERSAO },
            normaVersaoData = root.str("normaVersaoData").ifBlank { NrVisualCatalog.NORMA_VERSAO_DATA },
            observacoesTst = root.str("observacoesTst"),
            objetosDetectados = root.strList("objetosDetectados"),
            resumoCena = root.str("resumoCena"),
            descricoesFotos = lerDescricoes(root.array("descricoesFotos")),
            perguntas = lerPerguntas(root.array("perguntas")),
            dataHorarioMs = root.long("dataHorarioMs") ?: currentTimeMillis(),
            latitude = root.double("latitude"),
            longitude = root.double("longitude")
        )
    }

    /** Resposta de POST /nr-visual/analyze: { findings, objetosDetectados, ... }. */
    fun achadosFromAnalyzeResponse(json: String): List<NrAchado> =
        analyzeResultFromResponse(json).findings

    fun analyzeResultFromResponse(json: String): NrVisualAnalyzeResult {
        val root = JsonMap.parse(json)
        val objetos = root.strList("objetosDetectados")
        val findings = lerAchados(root.array("findings") ?: root.array("achados"))
        val filtrados = NrVisualObjectMap.reforcarRegrasDuras(
            NrVisualObjectMap.filtrarAchados(findings, objetos),
            objetos
        )
        return NrVisualAnalyzeResult(
            findings = filtrados,
            objetosDetectados = objetos,
            cenaSugerida = root.str("cenaSugerida").ifBlank { root.str("cenaTipo") },
            resumoCena = root.str("resumoCena"),
            modo = root.str("modo"),
            mensagem = root.str("mensagem")
        )
    }

    private fun lerAchados(arr: List<Any?>?): List<NrAchado> {
        if (arr == null) return emptyList()
        return arr.mapNotNull { node ->
            val o = node as? JsonMap ?: return@mapNotNull null
            val catalogId = o.str("catalogId").ifBlank { o.str("id") }
            val cat = NrVisualCatalog.porId(catalogId)
            NrAchado(
                id = o.str("uuid").ifBlank { o.str("achadoId").ifBlank { randomUuid() } },
                catalogId = catalogId,
                titulo = o.str("titulo").ifBlank { cat?.titulo.orEmpty() },
                nr = o.str("nr").ifBlank { cat?.nr.orEmpty() },
                citar = o.str("citar").ifBlank { cat?.citar.orEmpty() },
                gravidade = NrGravidade.fromName(o.str("gravidade"))
                    ?: cat?.gravidadePadrao
                    ?: NrGravidade.MEDIA,
                oQueViu = o.str("oQueViu").ifBlank { o.str("evidence").ifBlank { cat?.oQueProcurar.orEmpty() } },
                acaoSugerida = o.str("acaoSugerida").ifBlank { o.str("acao").ifBlank { cat?.acao.orEmpty() } },
                confianca = o.double("confianca") ?: o.double("confidence") ?: 0.4,
                fotoId = o.str("fotoId").takeIf { it.isNotBlank() },
                status = NrAchadoStatus.fromName(o.str("status")) ?: NrAchadoStatus.PENDENTE,
                notaTst = o.str("notaTst"),
                parecer = NrParecer.fromName(o.str("parecer")),
                objetoDetectado = o.str("objetoDetectado")
            )
        }
    }

    private fun achadoToJson(a: NrAchado): String = buildString {
        append('{')
        field("id", a.id); append(',')
        field("catalogId", a.catalogId); append(',')
        field("titulo", a.titulo); append(',')
        field("nr", a.nr); append(',')
        field("citar", a.citar); append(',')
        field("gravidade", a.gravidade.name); append(',')
        field("oQueViu", a.oQueViu); append(',')
        field("acaoSugerida", a.acaoSugerida); append(',')
        append("\"confianca\":"); append(a.confianca)
        if (a.fotoId != null) {
            append(','); field("fotoId", a.fotoId)
        }
        append(','); field("status", a.status.name)
        append(','); field("notaTst", a.notaTst)
        append(','); field("parecer", a.parecer.name)
        append(','); field("objetoDetectado", a.objetoDetectado)
        append('}')
    }

    private fun fotosToJson(fotos: List<FotoAnexo>): String = buildString {
        append('[')
        fotos.forEachIndexed { i, f ->
            if (i > 0) append(',')
            append('{')
            field("id", f.id); append(',')
            field("path", f.path); append(',')
            append("\"capturadoEm\":"); append(f.capturadoEm)
            if (f.latitude != null) {
                append(','); append("\"latitude\":"); append(f.latitude)
            }
            if (f.longitude != null) {
                append(','); append("\"longitude\":"); append(f.longitude)
            }
            val b64 = f.conteudoBase64
            if (!b64.isNullOrBlank()) {
                append(','); field("conteudoBase64", b64)
            }
            append('}')
        }
        append(']')
    }

    private fun lerDescricoes(arr: List<Any?>?): List<DescricaoFotoNr> {
        if (arr == null) return emptyList()
        return arr.mapNotNull { node ->
            val o = node as? JsonMap ?: return@mapNotNull null
            val fotoId = o.str("fotoId")
            if (fotoId.isBlank()) return@mapNotNull null
            DescricaoFotoNr(
                fotoId = fotoId,
                numero = o.long("numero")?.toInt() ?: 0,
                texto = o.str("texto"),
                objetos = o.strList("objetos")
            )
        }
    }

    private fun lerPerguntas(arr: List<Any?>?): List<PerguntaVisualNr> {
        if (arr == null) return emptyList()
        return arr.mapNotNull { node ->
            val o = node as? JsonMap ?: return@mapNotNull null
            val texto = o.str("texto")
            if (texto.isBlank()) return@mapNotNull null
            PerguntaVisualNr(
                id = o.str("id").ifBlank { randomUuid() },
                fotoId = o.str("fotoId"),
                fotoNumero = o.long("fotoNumero")?.toInt() ?: 0,
                objeto = o.str("objeto"),
                texto = texto,
                resposta = o.str("resposta")
            )
        }
    }

    private fun lerFotos(arr: List<Any?>?): List<FotoAnexo> {
        if (arr == null) return emptyList()
        return arr.mapNotNull { node ->
            val o = node as? JsonMap ?: return@mapNotNull null
            val path = o.str("path")
            if (path.isBlank()) return@mapNotNull null
            FotoAnexo(
                id = o.str("id").ifBlank { randomUuid() },
                path = path,
                capturadoEm = o.long("capturadoEm") ?: currentTimeMillis(),
                latitude = o.double("latitude"),
                longitude = o.double("longitude"),
                conteudoBase64 = o.str("conteudoBase64").takeIf { it.isNotBlank() }
            )
        }
    }

    private fun StringBuilder.field(name: String, value: String) {
        append('"'); append(name); append("\":\""); append(escape(value)); append('"')
    }

    private fun escape(s: String): String = buildString(s.length + 8) {
        for (c in s) {
            when (c) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> if (c.code < 0x20) {
                    append("\\u")
                    append(c.code.toString(16).padStart(4, '0'))
                } else append(c)
            }
        }
    }
}
